import { Box, For, HStack, Tabs, Text, useBreakpointValue, VStack } from '@chakra-ui/react';
import { isNotSectionTypeSensorOrSubInfo } from '@components/cockpit/panels/monitoring/monitoring.utils';
import { useMapContext } from '@components/map/MapContext';
import Commands from '@components/map/popup/infos/sensors/command/Commands';
import Information from '@components/map/popup/infos/sensors/information/Information';
import { DraggablePanel } from '@components/ui/draggable';
import { createErrorToastFromTradId } from '@components/ui/toaster';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { makeSelectPlatformsNamesByCode } from '@redux/config/config.selectors';
import {
  selectSensorConfigurationByUniqueKey,
  selectSensorOrSubSensorInfoByUniqueKey,
} from '@redux/situation/monitoring.selectors';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import { getSensorIconKey } from '@utils/map/sensor.utils';
import { hasSpecificState } from '@utils/sensors/sensors.utils';
import { useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormattedMessage } from 'react-intl';
import { shallowEqual } from 'react-redux';

import { MapIdEnum } from '@/types/map.types';
import { PopupPositionData, SensorInfoData } from '@/types/popup.types';

import Maintenance from './maintenance/Maintenance';
import SensorIcon from './SensorIcon';

function SensorInfoContent({
  mapConstraintsRef,
  featureValue: sensorUniqueKeys,
  top,
  left,
  onClose,
}: Readonly<SensorInfoData & PopupPositionData>) {
  const { mapId } = useMapContext();
  const sensorIconSize = useBreakpointValue<'lg' | 'sm'>({ base: 'sm', '4k': 'lg' });

  const uniqueKeys = useMemo(() => sensorUniqueKeys, [sensorUniqueKeys]);

  const sensorOrSubSensorInfo = useSelectorWithReplayMode(
    selectSensorOrSubSensorInfoByUniqueKey,
    uniqueKeys.sensorUniqueKey,
  );
  const parentConfiguration = useSelectorWithReplayMode(
    selectSensorConfigurationByUniqueKey,
    uniqueKeys.parentUniqueKey,
  );

  const platformCodes = useMemo(
    () => getPlatformsCodesFromMarks(parentConfiguration?.marks ?? []),
    [parentConfiguration?.marks],
  );

  const selectPlatforms = useMemo(makeSelectPlatformsNamesByCode, []);
  const platforms = useAppSelector((state) => selectPlatforms(state, platformCodes), shallowEqual);

  if (!sensorOrSubSensorInfo || !isNotSectionTypeSensorOrSubInfo(sensorOrSubSensorInfo)) {
    onClose();
    return null;
  }

  const commands = Commands({ sensorOrSubSensorInfo, mapId });
  const maintenance = Maintenance({ sensorOrSubSensorInfo, position: { x: left, y: top }, mapId });

  const status = sensorOrSubSensorInfo.status;
  const effectiveStatus = status?.effectiveStatus;

  const isActive = sensorOrSubSensorInfo.active;
  const isInMaintenance = sensorOrSubSensorInfo.maintenance;

  return (
    <DraggablePanel.Root
      key={sensorUniqueKeys.sensorUniqueKey}
      constraintsRef={mapConstraintsRef}
      top={top}
      left={left}
      offsetY={-100}
      offsetX={50}
    >
      <DraggablePanel.Header onClose={onClose}>
        <FormattedMessage id={`sensors.type.${sensorOrSubSensorInfo.configuration.type}`} />
      </DraggablePanel.Header>
      <DraggablePanel.Body width={{ base: '460px', '4k': '580px' }}>
        <HStack
          width="100%"
          gap="standardS"
          height={{ base: '104px', '4k': '120px' }}
          backgroundColor="neutral.700"
          paddingX="standardM"
          cursor="default"
        >
          <Box flexShrink={0}>
            <SensorIcon
              sensorIconName={getSensorIconKey(
                sensorOrSubSensorInfo.configuration.type,
                isActive,
                isInMaintenance,
                status && hasSpecificState(status) ? status.specificState : undefined,
                effectiveStatus,
              )}
              size={sensorIconSize}
            />
          </Box>
          <VStack width="100%" gap={0.5} alignItems="start" cursor="default">
            <Text textStyle="screenStd" paddingX={0.5} paddingY={0.25} backgroundColor="neutral.black" lineClamp={2}>
              {sensorOrSubSensorInfo.configuration.name}
            </Text>
            <HStack maxWidth="100%" gap={0.5}>
              <For each={platforms}>
                {(platform) => (
                  <Tooltip key={platform} content={platform} openDelay={200}>
                    <Text
                      paddingX={0.25}
                      backgroundColor="neutral.600"
                      textTransform="uppercase"
                      textStyle="screenStd"
                      fontWeight="medium"
                      color="neutral.200"
                    >
                      {platform.slice(0, 4)}
                    </Text>
                  </Tooltip>
                )}
              </For>
            </HStack>
          </VStack>
        </HStack>
        <Tabs.Root width="100%" size="screenStd" variant="neutral" height="max-content" defaultValue="information">
          <Tabs.List>
            <Tabs.Trigger value="information">
              <FormattedMessage id="sensors.tab.information.title" />
            </Tabs.Trigger>
            {commands !== null && (
              <Tabs.Trigger value="commands">
                <FormattedMessage id="sensors.tab.commands.title" />
              </Tabs.Trigger>
            )}
            {maintenance !== null && (
              <Tabs.Trigger value="maintenance">
                <FormattedMessage id="sensors.tab.maintenance.title" />
              </Tabs.Trigger>
            )}
          </Tabs.List>
          <Tabs.ContentGroup>
            <Tabs.Content value="information">
              <Information sensorOrSubSensorInfo={sensorOrSubSensorInfo} />
            </Tabs.Content>
            <Tabs.Content value="commands">{commands}</Tabs.Content>
            <Tabs.Content value="maintenance">{maintenance}</Tabs.Content>
          </Tabs.ContentGroup>
        </Tabs.Root>
      </DraggablePanel.Body>
    </DraggablePanel.Root>
  );
}

export default function SensorInfo(props: Readonly<SensorInfoData & PopupPositionData>) {
  const { mapId } = useMapContext();

  return mapId === MapIdEnum.REPLAY ? (
    <ErrorBoundary fallback={<></>} onError={() => createErrorToastFromTradId('error.sensorReplay.info')}>
      <SensorInfoContent {...props} />
    </ErrorBoundary>
  ) : (
    <SensorInfoContent {...props} />
  );
}
